import { useContext, useEffect, useState } from 'react'

import {
  Box,
  Button,
  CheckBox,
  Input,
  Select,
  SelectOption,
  Text,
} from '@fairfashion/design-system'
import { Controller, useForm } from 'react-hook-form'
import { useTheme } from 'styled-components'

import { GlobalDataContext } from '../../../../../contexts/globalData'
import { CompanySegment } from '../../../../../sdk/common/company.enum'
import { ICompany, IFarmData } from '../../../../../sdk/company/company.types'
import { sdk } from '../../../../../services/sdk'

import { AddProviderFormProps } from './add-provider-form.types'

export const AddProviderForm = ({
  data,
  alreadyExists,
  setOpen,
  setShowSearch,
  loading,
  onSubmit,
}: AddProviderFormProps) => {
  const theme = useTheme()
  const [segmentsOptions, setSegmentsOptions] = useState<SelectOption[]>([])
  const [checkbox, setCheckbox] = useState(false)
  const { companySegmentType } = useContext(GlobalDataContext)

  const { register, reset, setValue, control, handleSubmit, watch, getValues } =
    useForm<ICompany>({ defaultValues: {} as ICompany })

  useEffect(() => {
    setSegmentsOptions(
      Object.keys(companySegmentType).map((segment) => ({
        label:
          companySegmentType[segment as keyof typeof companySegmentType]
            .translate,
        value:
          companySegmentType[segment as keyof typeof companySegmentType].name,
      })),
    )
  }, [])

  useEffect(() => {
    data && reset({ ...data })
  }, [data])

  useEffect(() => {
    if (segmentsOptions.length) {
      setValue('segment', data?.segment)
    }
  }, [segmentsOptions])

  const { taxId } = watch()

  const rawTaxId = taxId?.replace(/[^a-zA-Z0-9]/g, '')

  const submit = async (dataForm: ICompany) => {
    if (alreadyExists) {
      try {
        await sdk.company.createCompanyProviderForUser(rawTaxId)
        await onSubmit(rawTaxId)
        setOpen(false)
      } catch (error) {}
    } else {
      try {
        let farmData: IFarmData | undefined = undefined

        if (
          dataForm.farmData?.nationalRegistry ||
          dataForm.farmData?.ownerName
        ) {
          farmData = dataForm.farmData
        }

        await sdk.company.createCompany({
          name: dataForm.name,
          taxId: rawTaxId,
          segment: dataForm.segment,
          email: dataForm.email ?? '',
          address: dataForm.address ?? {},
          farmData: farmData,
        })
        await sdk.company.createCompanyProviderForUser(rawTaxId)

        await onSubmit(rawTaxId)
        setOpen(false)
      } catch (error) {
        console.log(error)
      }
    }
  }

  const fetchZipcode = async (code: string) => {
    const codeRaw = code ? code.replace(/\D/g, '') : ''
    if (codeRaw?.length === 8) {
      try {
        const response = await sdk.providers.getZipcode(codeRaw)
        setValue('address', {
          ...response,
          zipCode: code,
          district: response.neighborhood,
          country: 'Brasil',
        })
      } catch (err) {
        setValue('address', { zipCode: code })
      }
    } else {
      setValue('address', { zipCode: code })
    }
  }

  const segment = getValues('segment')

  const isFarm = segment === CompanySegment.FARM

  return (
    <Box width="100%" direction="column" gap={2}>
      <form onSubmit={handleSubmit(submit)}>
        <Box width="100%" direction="column" gap={2}>
          <Box width="100%" gap={2} flexWrap="wrap">
            <Box minWidth="250px" flex={1}>
              <Input
                {...register('taxId')}
                label="Documento"
                disabled={alreadyExists}
                value={getValues('taxId')}
                required
              />
            </Box>
            <Box flex={1} minWidth="250px">
              <Input
                {...register('name')}
                label="Razão Social"
                disabled={alreadyExists}
                value={getValues('name')}
                required
              />
            </Box>
          </Box>
          <Box width="100%" gap={2} flexWrap="wrap">
            <Box minWidth="250px" flex={1}>
              <Input
                {...register('email')}
                label="E-mail"
                disabled={alreadyExists}
                value={getValues('email')}
                required
              />
            </Box>
            <Box minWidth="250px" flex={1}>
              <Controller
                control={control}
                name="segment"
                disabled={alreadyExists}
                render={({ field }) => {
                  return (
                    <Select
                      options={segmentsOptions}
                      label="Segmento"
                      placeholder="Escolha uma opção"
                      required
                      {...field}
                    />
                  )
                }}
              />
            </Box>
          </Box>
          <Box width="100%" gap={2} flexWrap="wrap">
            <Box minWidth="120px" flex={1}>
              <Input
                {...register('address.zipCode')}
                label="Código postal"
                disabled={alreadyExists}
                onChange={(e) => fetchZipcode(e.target.value)}
                value={getValues('address.zipCode')}
                required
              />
            </Box>
            <Box minWidth="120px" flex={1}>
              <Input
                {...register('address.state')}
                label="Cidade"
                disabled={alreadyExists}
                value={getValues('address.state')}
                required
              />
            </Box>
            <Box minWidth="120px" flex={1}>
              <Input
                {...register('address.city')}
                label="Estado"
                disabled={alreadyExists}
                value={getValues('address.city')}
                required
              />
            </Box>
            <Box minWidth="120px" flex={1}>
              <Input
                {...register('address.country')}
                label="País"
                disabled={alreadyExists}
                value={getValues('address.country')}
                required
              />
            </Box>
          </Box>
          <Box width="100%" gap={2} flexWrap="wrap">
            <Box flex={6} minWidth="250px">
              <Input
                {...register('address.street')}
                label="Logradouro"
                disabled={alreadyExists}
                value={getValues('address.street')}
                required
              />
            </Box>
            <Box flex={1}>
              <Input
                {...register('address.number')}
                label="Número"
                disabled={alreadyExists}
                value={getValues('address.number')}
                required
              />
            </Box>
            <Box flex={3} minWidth="120px">
              <Input
                {...register('address.district')}
                label="Bairro"
                disabled={alreadyExists}
                value={getValues('address.district')}
                required
              />
            </Box>
          </Box>
          {isFarm && (
            <Box width="100%" gap={2} flexWrap="wrap">
              <Box flex={3}>
                <Input
                  {...register('farmData.nationalRegistry')}
                  label="CAR"
                  disabled={alreadyExists}
                  value={getValues('farmData.nationalRegistry')}
                />
              </Box>
              <Box flex={2}>
                <Input
                  {...register('farmData.ownerName')}
                  label="Nome do Produtor"
                  disabled={alreadyExists}
                  value={getValues('farmData.ownerName')}
                />
              </Box>
            </Box>
          )}
          {!alreadyExists && (
            <CheckBox
              onChange={(e) => setCheckbox(e.target.checked)}
              label="Declaro que os dados são verdadeiros e me responsabilizo pelas informações aqui contidas."
            />
          )}
          {alreadyExists && (
            <Box width="100%">
              <Text size="small" color="error">
                *Não é possível alterar os dados, pois essa empresa já está
                cadastrada.
              </Text>
            </Box>
          )}
          <Box width="100%" gap={1} justifyContent="flex-end">
            <Button
              padding={`${theme.spacing['1']} ${theme.spacing['2']}`}
              onClick={() => setShowSearch(true)}
              variant="outlined"
            >
              Voltar
            </Button>
            <Button
              padding={`${theme.spacing['1']} ${theme.spacing['2']}`}
              type="submit"
              disabled={!checkbox && !alreadyExists}
              loading={loading}
            >
              Salvar
            </Button>
          </Box>
        </Box>
      </form>
    </Box>
  )
}
